import "./style.scss";
import * as React from "react";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { Link, useHistory, useLocation } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { handleDrawer } from "../../../redux/actions/drawerAction";
import { useStyles } from "./style";
import { RootState } from "../../../redux/store";
import { IUser } from "../../../types/user";
import { Box } from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import DashboardIcon from "../../../components/Icons/DashboardIcon";
import HelpIcon from "../../../components/Icons/HelpIcon";
import HamburgerIcon from "../../../components/Icons/HamburgerIcon";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { getTimeBasedGreeting } from "../../../util/DateTimeUtil";
import logoImage from "../../../assets/logo.png";
import ProfileDrawer from "../Account/setting";
import HomeIcon from "../../../components/Icons/HomeIcon";
import Notification from "../../../components/Icons/Notification";
import SchoolNotice from "../../../components/Icons/SchoolNotice";
import SettingBlueIcon from "../../../components/Icons/SettingIconBlue";
import HelpSupportIcon from "../../../components/Icons/HelpSupportIcon";
import LogoutRedIcon from "../../../components/Icons/LogoutRedIcon";
import { logoutAndRedirect } from "../../../util/logout";
import FaqIcon from "../../../components/Icons/FaqIcon";
import SupportTicketIcon from "../../../components/Icons/SupportTicketIcon";
import OnboardingIcon from "../../../components/Icons/OnboardingIcon";
import {
  channelPostDetails,
  getActiveChannel,
  getChannelActivePost,
  getChannelPostList,
  getProgramById,
  getProgramChannelPostList,
} from "../../../redux/actions/programAction";
import EditProfileIcon from "../../../components/Icons/EditProfileIcon";
import ChannelIcon from "../../../components/Icons/ChannelIcon";
import CloseLockIcon from "../../../components/Icons/CloseLockIcon";
import AddChannelIcon from "../../../components/Icons/AddChannelIcon";
import ChannelListItem from "./component/channelList";
import { useIsMobile } from "../../../util/hooks";
import { assetUrl } from "../../../util/assetUrl";
import { usePrivileges } from "../../../util/usePrivileges";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  if (p.startsWith("/assets") || p.startsWith("/uploads")) return `${ASSET_BASE}${p}`;
  try {
    return assetUrl(p);
  } catch {
    return p;
  }
};

interface PropsType {
  isOpen: boolean;
  user: IUser;
  loader: string;
  handleDrawer: (isOpen: boolean) => any;
}

// tiny color helper (invert for legible text on secondaryColor)
const invertColor = (hex: string) => {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map((c) => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const normalizeChannelId = (value?: string | null) =>
  (value ?? '')
    .toString()
    .split(':')
    .pop()
    ?.toLowerCase() || '';

function ResponsiveDrawer(props: PropsType) {
  const classes = useStyles(),
    history = useHistory(),
    location = useLocation(),
    dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { singleProgram, programChannelPost, subscribeChannels, channelDetails } = useSelector(
    (state: RootState) => state.programReducer
  );
  const { isOpen } = useSelector((state: RootState) => state.drawerReducer);

  const { user } = useSelector((state: RootState) => state.auth);

  const [channels, setChannels] = React.useState<any[]>([]);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const [activeChannel, setActiveChannel] = React.useState<any>({});
  const { communityIdentifier } = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
  const urlParams = React.useMemo(() => new URLSearchParams(window.location.search), [window.location.search]);
  const channelIdentifier = urlParams.get("channelIdentifier");
  const channelContextId = urlParams.get("contextId");
  const { _id: programId } = singleProgram || ({} as any);

  const {
    privileges,
    fallbackPlatformAdmin,
    fallbackCommunityAdmin,
    communityRole,
    communityModeratedChannels,
    communityEnrollment,
    communityVisibleChannelIds,
  } = usePrivileges(communityIdentifier || undefined);

  const normalizedUserType = (user?.type || '').trim().toLowerCase();
  const isTeamAdmin = normalizedUserType === 'team-admin';

  const normalizedCommunityRole = (communityRole || '').toString().trim().toLowerCase();
  const elevatedOverrides = React.useMemo(
    () =>
      communityModeratedChannels.filter((channel) => {
        const role = (channel.role || 'moderator').toString().trim().toLowerCase();
        return role === 'admin' || role === 'moderator';
      }),
    [communityModeratedChannels]
  );
  const hasChannelOverrides = elevatedOverrides.length > 0;
  const accessibleOverrideChannels = React.useMemo(() => {
    if (privileges.platformAdmin || privileges.hasRole('admin', 'CommunityAdmin')) {
      return elevatedOverrides;
    }
    return elevatedOverrides.filter((channel) =>
      privileges.canViewChannel(communityIdentifier || '', channel.channelIdentifier)
    );
  }, [
    elevatedOverrides,
    privileges,
    communityIdentifier,
  ]);

  const hasAccessibleOverrides = accessibleOverrideChannels.length > 0;
  const isModeratorRole = normalizedCommunityRole === 'moderator' || hasAccessibleOverrides;
  const isCommunityAdminRole =
    normalizedCommunityRole === 'communityadmin' ||
    privileges.isCommunityAdmin(communityIdentifier || undefined) ||
    fallbackCommunityAdmin;
  const isPlatformAdmin = privileges.platformAdmin || fallbackPlatformAdmin;

  const canManageCommunityWide = isPlatformAdmin || isCommunityAdminRole || isTeamAdmin;
  const showManageButton = canManageCommunityWide || isModeratorRole;
  const isModeratorOnly = isModeratorRole && !canManageCommunityWide;
  const manageButtonLabel = canManageCommunityWide ? 'Manage Community' : 'Manage Channels';

  React.useEffect(() => {
    alterChannelList();
  }, [
    subscribeChannels,
    singleProgram,
    privileges,
    communityIdentifier,
    fallbackPlatformAdmin,
    fallbackCommunityAdmin,
    elevatedOverrides,
    canManageCommunityWide,
    isModeratorRole,
    communityVisibleChannelIds,
  ]);

  const alterChannelList = () => {
    const activeSubscriptions = (subscribeChannels || [])
      .filter((item: any) => item && item.isActive !== false)
      .map((item: any) => normalizeChannelId(item.channelIdentifier));
    const subscriptionSet = new Set(activeSubscriptions);

  const visibleChannelIdSet = new Set(
    (communityVisibleChannelIds || []).map((id: string) => normalizeChannelId(id))
  );
  const enrollmentChannelIdSet = new Set(
    ((communityEnrollment?.channels as any[]) || [])
      .map((entry) => normalizeChannelId(entry?.channelIdentifier))
      .filter(Boolean)
  );

    const communityChannels = (singleProgram?.communities || [])
      .find((c: any) => c?.communityIdentifier === communityIdentifier)?.channels || [];

    let candidateChannels = (communityChannels || []).filter((item: any) => {
      if (!item || item.publish === false) return false;
      const normalized = normalizeChannelId(item.channelIdentifier);
      // Only show channels the user has actually joined/enrolled in
      // Don't show all visible channels - those are for the onboarding page to display options
      return (
        subscriptionSet.has(normalized) ||
        enrollmentChannelIdSet.has(normalized)
      );
    });

    const bypassVisibility = isPlatformAdmin || isTeamAdmin;

    if (!bypassVisibility) {
      candidateChannels = candidateChannels.filter((item: any) =>
        privileges.canViewChannel(communityIdentifier || '', item.channelIdentifier)
      );
    }

    const userId = JSON.parse(localStorage.getItem("authInfo") || "{}")._id;
    candidateChannels.forEach((item: any) => {
      dispatch(getChannelPostList(programId as any, userId as any, communityIdentifier, item?.channelIdentifier));
    });

    setChannels(candidateChannels);
  };

  const joinChannel = () => {
    const searchParams = new URLSearchParams();
    searchParams.set("community", communityIdentifier);
    history.push(`/onboarding?${searchParams.toString()}`);
  };

  const manageChannels = () => {
    const searchParams = new URLSearchParams();
    searchParams.set("community", communityIdentifier);
    history.push(`/admin/channel/list?${searchParams.toString()}`);
  };

  const joinCommunity = () => {
    const searchParams = new URLSearchParams();
    searchParams.set("activeCommunity", communityIdentifier);
    history.push(`/onboarding?${searchParams.toString()}`);
  };

  const userId = JSON.parse(localStorage.getItem("authInfo") || "{}")?.["_id"];
  const nestedItems: any[] = [];
  const settingItems = [
    { label: "Dashboard", icon: HomeIcon, path: "/user/dashboard" },
    {
      label: "Settings",
      icon: SettingBlueIcon,
      nested: true,
      nestedItem: [
        { label: "General Setting", icon: SettingBlueIcon, path: "/user/appSetting" },
        { label: "Edit profile", icon: EditProfileIcon, path: "/user/editProfile" },
      ],
    },
    {
      label: "Help and Support",
      icon: HelpSupportIcon,
      nested: true,
      nestedItem: [{ icon: HelpSupportIcon, label: "Help Center", path: "/user/support" }],
    },
    { label: "Logout", icon: LogoutRedIcon, className: "danger", onClick: () => logoutAndRedirect() },
  ];

  // Admin menu items
  const [adminMenuAnchor, setAdminMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [viewMode, setViewMode] = React.useState<"member" | "admin">("member");

  const adminMenuItems = [
    {
      label: "Manage Channels",
      onClick: () => {
        const searchParams = new URLSearchParams();
        searchParams.set("community", communityIdentifier);
        history.push(`/admin/channel/list?${searchParams.toString()}`);
        setAdminMenuAnchor(null);
      },
    },
    {
      label: "Manage Invitations",
      onClick: () => {
        const searchParams = new URLSearchParams();
        searchParams.set("community", communityIdentifier);
        history.push(`/admin/invitations?${searchParams.toString()}`);
        setAdminMenuAnchor(null);
      },
    },
    {
      label: "Community Roles",
      onClick: () => {
        const searchParams = new URLSearchParams();
        searchParams.set("community", communityIdentifier);
        history.push(`/admin/community/roles?${searchParams.toString()}`);
        setAdminMenuAnchor(null);
      },
    },
    {
      label: "Theme Editor",
      onClick: () => {
        const pid = singleProgram?._id;
        if (pid) {
          history.push(`/admin/programs/${pid}/communities/${communityIdentifier}/theme`);
        }
        setAdminMenuAnchor(null);
      },
    },
  ];

  const staticSideBarItems: any[] = [
    ...(showManageButton ? [{
      type: "admin-toggle",
      label: viewMode === "admin" ? "Member View" : "Admin View",
      icon: <DashboardIcon />,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        setAdminMenuAnchor(event.currentTarget);
      },
    }] : []),
    {
      type: "item",
      path: "/user/support",
      label: "Support",
      icon: <HelpIcon />,
      onClick: () => handleNavigate("/user/support"),
    },
    {
      type: "item",
      onClick: () => logOut(),
      label: "Sign Out",
      icon: <ExitToAppIcon style={{ width: "24px", height: "21px" }} />,
    },
  ];

  const communityTheme = React.useMemo(() => {
    const community = singleProgram?.communities?.find(
      (c: any) => c?.communityIdentifier === communityIdentifier
    );
    return {
      mainColor: community?.communityStyle?.mainColor || "#1976d2",
      secondaryColor: community?.communityStyle?.secondaryColor || "#ffffff",
      fontStyle: community?.communityStyle?.fontStyle || "Arial",
      headerImage: community?.communityStyle?.headerImage || "",
    };
  }, [singleProgram, communityIdentifier]);

  const fgOnSecondary = React.useMemo(
    () => invertColor(communityTheme.secondaryColor),
    [communityTheme.secondaryColor]
  );

  const activeCommunity = React.useMemo(
    () => singleProgram?.communities?.find((c: any) => c?.communityIdentifier === communityIdentifier),
    [singleProgram, communityIdentifier]
  );

  const [logoBust, setLogoBust] = React.useState<number>(0);
  React.useEffect(() => {
    setLogoBust(Date.now());
  }, [activeCommunity?.communityStyle?.logo, communityIdentifier]);
  const communityLogoUrl = React.useMemo(() => {
    const raw = (activeCommunity as any)?.communityStyle?.logo || "";
    const resolved = resolveHeaderUrl(raw);
    if (!resolved) return "";
    return logoBust ? `${resolved}${resolved.includes("?") ? "&" : "?"}v=${logoBust}` : resolved;
  }, [activeCommunity?.communityStyle?.logo, logoBust]);

  const generateSidebarItems = (channelsIn: any[]): any[] => {
    try {
      return channelsIn?.map((channel: any, idx: number) => {
        const sidebarItem: any = {
          type: "collapse",
          channelIdentifier: channel.channelIdentifier,
          label: channel.channelName,
          description: channel.channelDetails?.description || `Stay updated on ${channel.channelName}`,
          iconClass: channel.channelPostingSettings?.defaultIcon || "note",
          icon: (
            <i className="material-icons icon" style={{ fontSize: "32px", padding: "8px", margin: "4px" }}>
              {channel.channelPostingSettings?.defaultIcon || "note"}
            </i>
          ),
          isOpen: openIndex === idx,
          onClick: () => handleChannel(idx, channel),
          alert: false,
          channelFormSettings: channel?.channelFormSettings || null,
          channelPostingSettings: channel?.channelPostingSettings || null,
          channelPrivacySettings: channel?.channelPrivacySettings || null,
          channelStyle: channel.channelStyle || {
            mainColor: communityTheme.mainColor,
            secondaryColor: communityTheme.secondaryColor,
            headerImage: communityTheme.headerImage,
            fontStyle: communityTheme.fontStyle,
          },
          nestedItems: nestedItems.map((item: any) => ({
            path: `/user/post/${item.label.toLowerCase()}`,
            label: item.label,
            description: item.description,
            image: item.icon,
            alert: item.alert,
            channelPostingSettings: channel?.channelPostingSettings || null,
            channelPrivacySettings: channel?.channelPrivacySettings || null,
          })),
        };
        if (channel?.channelIdentifier) {
          (sidebarItem as any)[channel.channelIdentifier] = programChannelPost[channel.channelIdentifier] || [];
        }
        return sidebarItem;
      });
    } catch (error) {
      console.error("Error generating sidebar items:", error);
      return [];
    }
  };
  const sidebarChannels = generateSidebarItems(channels || []);

  React.useEffect(() => {
    if (window.location.pathname === "/user/post") {
      updatePostDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelIdentifier, channelContextId, (programChannelPost as any)[channelIdentifier as any]]);

  React.useEffect(() => {
    if (window.location.pathname === "/user/channel") {
      updateChannelDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelIdentifier, singleProgram, sidebarChannels]);

  React.useEffect(() => {
    if (!singleProgram) {
      const activeProgramEnrollment = JSON.parse(localStorage.getItem("activeProgramEnrollment") || "{}");
      dispatch(getProgramById(activeProgramEnrollment.programId));
      updateChannelDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleProgram]);

  const updatePostDetails = async () => {
    const channelData: any =
      sideBarItems.find((item: any) => item.channelIdentifier === channelIdentifier) || sideBarItems[0];
    const channelIndex = channelData?.channelIdentifier;

    let currentPost: any =
      channelIdentifier && channelContextId
        ? channelData[channelIndex]?.find((item: any) => item.contextId === channelContextId) || {}
        : channelData[channelIndex]?.[0] || {};
    if (Object.keys(currentPost).length !== 0 && (currentPost as any).contextId !== channelContextId) {
      let postDetails: any = await dispatch(
        channelPostDetails(currentPost.programId, currentPost.userId, currentPost.contextId)
      );
      if (postDetails) {
        currentPost = postDetails;
      }
    }

    Object.assign(currentPost, {
      channelPostingSettings: channelData.channelPostingSettings,
      channelPrivacySettings: channelData.channelPrivacySettings,
      channelIdentifier: channelData.channelIdentifier,
      channelFormSettings: channelData.channelFormSettings,
      channelStyle: channelData.channelStyle || {
        mainColor: communityTheme.mainColor,
        secondaryColor: communityTheme.secondaryColor,
        headerImage: communityTheme.headerImage,
        fontStyle: communityTheme.fontStyle,
      },
    });
    dispatch(getChannelActivePost(currentPost));
  };

  const updateChannelDetails = () => {
    const channelData: any =
      sideBarItems.find((item: any) => item.channelIdentifier === channelIdentifier) || sideBarItems[0];
    if (channelData) {
      channelData.channelStyle = channelData.channelStyle || {
        mainColor: communityTheme.mainColor,
        secondaryColor: communityTheme.secondaryColor,
        headerImage: communityTheme.headerImage,
        fontStyle: communityTheme.fontStyle,
      };
    }
    if (channelData?.["channelIdentifier"] === channelIdentifier && (channelDetails as any)?.["channelIdentifier"] !== channelIdentifier) {
      dispatch(getActiveChannel(channelData));
      setActiveChannel(channelData?.channelIdentifier);
    }
  };

  const handleDrawerOpen = () => {
    setProfileOpen(true);
  };
  const handleProfileClose = () => {
    setProfileOpen(false);
  };
  const handleChannel = (idx: number, channel: any) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
    dispatch(getActiveChannel(channel));
    dispatch({
      type: "SET_CHANNEL_THEME",
      channelId: channel.channelIdentifier,
      payload: channel.channelStyle || {
        mainColor: communityTheme.mainColor,
        secondaryColor: communityTheme.secondaryColor,
        headerImage: communityTheme.headerImage,
        fontStyle: communityTheme.fontStyle,
      },
    });
    history.push(`/user/channel?channelIdentifier=${channel.channelIdentifier}`);
    setActiveChannel(channel.channelIdentifier);
  };
  const logOut = () => {
    localStorage.clear();
    window.location.href = "/login";
  };
  const toggleDrawer = () => () => {
    dispatch(handleDrawer(!isOpen));
  };
  const isActive = (path: string) => {
    const { pathname, search } = location as any;
    return pathname === path || (pathname.startsWith(path) && search === new URLSearchParams(path).toString());
  };
  const handleNavigate = (path: string) => {
    history.push(path);
  };

  let sideBarItems: any[] =
    (sidebarChannels && sidebarChannels.length > 0) ? [...sidebarChannels, ...staticSideBarItems] : staticSideBarItems;

  return (
    <>
      <Drawer
        className={`${classes.drawer} sidebar_Wrapper`}
        variant={isMobile ? "temporary" : "permanent"}
        open={isOpen}
        onClose={toggleDrawer()}
        classes={{ paper: classes.drawerPaper }}
        PaperProps={{
          style: {
            backgroundColor: communityTheme.secondaryColor,
            color: fgOnSecondary,
            fontFamily: communityTheme.fontStyle,
          },
        }}
      >
        <List className={classes.list}>
          <ListItem style={{ justifyContent: "center" }}>
            <Link to="/" className={classes.logo}>
              <div className={classes.logoWrapper}>
                <img src={communityLogoUrl || logoImage} alt="Logo" className={classes.logoImage} style={{
    width: "300",
    height: "auto",
    display: "block",
    alignItems: "center",
    justifyContent: "center"
  }}/>
              </div>
            </Link>
          </ListItem>
          <ListItem>
            <Avatar
              alt={user?.name}
              className={classes.avatar}
              src={(process.env.REACT_APP_API_URL || "") + "/" + (user as any)?.avatarUrl}
              onClick={handleDrawerOpen}
            />
            <div>
              <span className={classes.grey} style={{ color: `${fgOnSecondary}CC` }}>{getTimeBasedGreeting()}!</span>
              <div className={classes.profileName} style={{ color: fgOnSecondary }}>
                {user && `Welcome ${user?.name} ${user?.lastName ? user.lastName : ""}!`}
              </div>
              <div
                className={classes.communityIdentifier}
                onClick={() => joinCommunity()}
                style={{ cursor: "pointer", color: communityTheme.mainColor }}
              >
                {communityIdentifier}
              </div>
            </div>
          </ListItem>

          <Box style={{ display: "flex" }}>
            <ListItem style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}>
              <span className={classes.channelTitle} style={{ color: fgOnSecondary }}>My Channels</span>
              <Box className={classes.addChannel} onClick={() => joinChannel()}>
                <span className={classes.channelSmallTitle} style={{ color: communityTheme.mainColor }}>Join Channel</span>
                <AddChannelIcon width={30} height={30} />
              </Box>
              {showManageButton && (
                <Box className={classes.addChannel} onClick={() => manageChannels()}>
                  <span className={classes.channelSmallTitle} style={{ color: communityTheme.mainColor }}>
                    {manageButtonLabel}
                  </span>
                  <CloseLockIcon width={30} height={30} />
                </Box>
              )}
            </ListItem>
          </Box>

          {/* Show empty state message when user has no channels */}
          {channels.length === 0 && (
            <ListItem
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "24px 16px",
                margin: "16px 8px",
                backgroundColor: `${communityTheme.mainColor}15`,
                border: `2px dashed ${communityTheme.mainColor}40`,
                borderRadius: 12,
                cursor: "pointer",
              }}
              onClick={() => joinChannel()}
            >
              <AddChannelIcon width={48} height={48} />
              <Box style={{ textAlign: "center", marginTop: 12 }}>
                <div style={{
                  color: fgOnSecondary,
                  fontWeight: 600,
                  fontSize: 16,
                  marginBottom: 8,
                  fontFamily: communityTheme.fontStyle
                }}>
                  No Channels Yet
                </div>
                <div style={{
                  color: `${fgOnSecondary}CC`,
                  fontSize: 14,
                  fontFamily: communityTheme.fontStyle
                }}>
                  Click here to browse and join channels
                </div>
              </Box>
            </ListItem>
          )}

          {sideBarItems.map((item: any, index: number) => {
            switch (item.type) {
              case "item":
                return (
                  <ListItem
                    key={index}
                    className={`${classes.listItem} ${item.path ? (isActive(item.path) ? "active" : "") : ""}`}
                    button
                    onClick={item.onClick}
                    style={{
                      backgroundColor: `${fgOnSecondary}0D`,
                      border: `1px solid ${fgOnSecondary}22`,
                      borderRadius: 12,
                      margin: "6px 8px",
                      color: fgOnSecondary,
                      fontFamily: communityTheme.fontStyle,
                    }}
                  >
                    {item.icon && (
                      <span style={{ marginRight: 8, color: communityTheme.mainColor }}>
                        {item.icon}
                      </span>
                    )}
                    <ListItemText
                      className={`${classes.listItemText}`}
                      primary={item.label}
                      primaryTypographyProps={{
                        style: { color: fgOnSecondary, fontFamily: communityTheme.fontStyle, fontWeight: 600 },
                      }}
                    />
                  </ListItem>
                );
              case "collapse":
                // Get the effective role for this channel (viewer, member, moderator, or admin)
                const channelRole = privileges.effectiveRoleForChannel(communityIdentifier || '', item.channelIdentifier);
                return (
                  <React.Fragment key={index}>
                    <ChannelListItem
                      key={item.channelIdentifier}
                      item={item}
                      activeChannel={activeChannel}
                      setActiveChannel={setActiveChannel}
                      programId={programId}
                      userId={userId}
                      communityIdentifier={communityIdentifier}
                      channelRole={channelRole}
                    />
                  </React.Fragment>
                );
              default:
                return null;
            }
          })}
        </List>
      </Drawer>

      <ProfileDrawer open={profileOpen} onClose={handleProfileClose} settingItems={settingItems} classes={classes} />
    </>
  );
}

const mapStateToProps = (store: RootState) => ({
  user: store.auth.user,
  isOpen: store.drawerReducer.isOpen,
  loader: store.drawerReducer.loader,
});

export default connect(mapStateToProps, {
  handleDrawer,
})(ResponsiveDrawer);
